import { useMemo } from 'react'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { PROGRESS_BAR_CHEST } from '~/bootcamp/constants/bootcamp.const'
import { Breakpoint, useCheckBreakpoint } from '~/utils'

import styles from './ProgressBarChest.module.css'

const ProgressBarChest = ({
  availableRewards = 0,
  collectedRewards = 0,
  correctConsecutiveAnswers = 0,
}): React.ReactElement => {
  const defaultColor = 'bg-purple'
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })

  const percentage: number = useMemo(() => {
    const consecutivePercentage = (correctConsecutiveAnswers / 3) * 100
    return Math.floor(consecutivePercentage)
  }, [correctConsecutiveAnswers])

  const getProgressBar = (index: number, collected: number): React.ReactElement => {
    const progress = index === collected ? percentage : index < collected ? 100 : 0
    return (
      <div
        className={`w-full ${index === 0 ? 'rounded-l' : ''} relative bg-black shadow-inner-lite bg-opacity-10 h-2 `}
      >
        <div
          className={`absolute top-0 bottom-0 left-0 rounded ${styles['animate-bar']} ${defaultColor}`}
          style={{ width: `${progress}%` }}
        />
        <div
          className={`absolute z-10 top-0 bottom-0 left-0 ${styles['animate-bar']} ${styles['stripe-gradient']}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    )
  }

  return (
    <div className="py-3 flex">
      {new Array(availableRewards).fill('').map((_, index) => {
        const totalRewards = collectedRewards + Math.floor(correctConsecutiveAnswers / 3)
        const isChestOpen = index < totalRewards
        const isSmallIcon = isMobile && availableRewards > 5
        const mediaQuery = [{ screen: '(min-width: 280px)', width: `${isSmallIcon ? 32 : 42}px` }]
        const isLast = index === availableRewards - 1

        return (
          <div className="w-full flex justify-end items-center" key={index.toString()}>
            <div
              className={`w-full ${
                index < totalRewards ? (index === 0 ? '-mr-4' : '-ml-1 -mr-4') : index === 0 ? '-mr-1' : '-mx-1'
              }`}
            >
              {getProgressBar(index, collectedRewards)}
            </div>

            {isLast ? (
              <ResponsiveImage
                alt="closed chest"
                className="flex-shrink-0 z-10 -ml-1 -mt-1"
                height={isSmallIcon ? (isChestOpen ? 37 : 23) : isChestOpen ? 64 : 40}
                imageWidths="64, 84"
                mediaQuery={mediaQuery}
                srcKey={isChestOpen ? PROGRESS_BAR_CHEST.openChestSrc : PROGRESS_BAR_CHEST.closedChestSrc}
                width={isSmallIcon ? 32 : 60}
              />
            ) : (
              <>
                {isChestOpen ? (
                  <ResponsiveImage
                    alt="star"
                    className="z-20 animate-scale-in -mt-1 -mr-2"
                    height={35}
                    imageWidths="35"
                    srcKey="icons/star.svg"
                    width={35}
                  />
                ) : (
                  <div className="flex flex-shrink-0 justify-center items-center h-5 w-5 rounded-full z-20 bg-gray-extra-light shadow-inner-lite-sm">
                    <div className="w-3.5 h-3.5 rounded-full bg-purple shadow-inner-lite animate-scale-in" />
                  </div>
                )}
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ProgressBarChest
